import { useTopLevelObjectType } from 'pages/flex-data/flex-data-links/useTopLevelObjectType';
import { useDocumentFlexDataLinks } from 'pages/flex-data/flex-data-links/useDocumentFlexDataLinks';
import type { VariableNode } from 'services/entities/VariablesEntity';

export type CrmEmptyState = 'not-connected' | 'no-deal' | 'array-empty' | null;

// 'Proposify' marks links to native, in-app entities rather than external CRM records —
// see useLinkCrmObject.ts, which hardcodes it as the fromObject of every link.
const isCrmLink = (dataSourceType: string) => dataSourceType !== 'Proposify';
const isDealLink = (normalizedType: string) => normalizedType === 'Opportunity';

export function useCrmEmptyState(contentId: string, arrayNode?: VariableNode, enabled = true) {
  const { topLevelObjectTypes, isTopLevelObjectTypesLoading } = useTopLevelObjectType(enabled);
  const { linkedObjects, isLoading: isLinkedObjectsLoading } = useDocumentFlexDataLinks(contentId, enabled);

  const isLoading = isTopLevelObjectTypesLoading || isLinkedObjectsLoading;
  const hasCrmConnected = topLevelObjectTypes.length > 0;
  const hasDealLinked = linkedObjects.some((link) => isCrmLink(link.dataSourceType) && isDealLink(link.normalizedType));
  const hasAnyCrmObjectLinked = linkedObjects.some((link) => isCrmLink(link.dataSourceType));

  let emptyState: CrmEmptyState = null;
  if (!isLoading) {
    if (!hasCrmConnected) {
      emptyState = 'not-connected';
    } else if (!hasDealLinked) {
      emptyState = 'no-deal';
    } else if (arrayNode && (arrayNode.variables?.length ?? 0) === 0) {
      emptyState = 'array-empty';
    }
  }

  return { emptyState, isLoading, hasAnyCrmObjectLinked };
}
